import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { PlayArrow, Schedule } from "@mui/icons-material"
import { v4 as uuidv4 } from "uuid"
import { SnackbarAlert } from "@/core/components"
import {
  ChainFormState,
  Descriptors,
  ActionNodeForm,
  validateChain,
  toChainDto,
  toChainTemplate,
} from "./chainSerializer"
import { Slot } from "./slot"
import { collectResultPaths } from "./resultPaths"
import { LaunchTargetFields } from "../common/launchTargetFields"
import { launchResultMessage } from "../common/launchResult"
import { Userbot, UserbotCatalog } from "../../interfaces"
import { useCreateChainMutation, useValidateChainMutation } from "../../store"
import { useCatalog, useUserbotWriteAccess } from "../../hooks"
import { getApiErrorMessage } from "@/core/utils"
import { mapZodErrors } from "./mapZodErrors"
import { launchIdentity } from "../common/launchIdentity"

const emptyActionNode = (): ActionNodeForm => ({
  key: uuidv4(),
  kind: "action",
  id: "",
  action: "",
  paramsValues: {},
  rawParamsJson: "",
  onError: "fail",
  retryAttempts: "",
  retryBackoffMs: "",
  saves: [],
})

const initialState = (): ChainFormState => ({
  executor: "client",
  clientId: "",
  role: "",
  name: "",
  comment: "",
  contextJson: "",
  scheduleDate: "",
  steps: [emptyActionNode()],
})

interface RunModeProps {
  mode: "run"
  extraHints?: string[]
  onLaunched?: (result: Userbot.LaunchResult) => void
}
interface TemplateModeProps {
  mode: "template"
  value?: ChainFormState
  extraHints?: string[]
  onChange: (
    state: ChainFormState,
    template: Userbot.ChainTemplate | null,
  ) => void
}
type IProps = RunModeProps | TemplateModeProps

/**
 * Конструктор цепочки.
 * mode="run" — сам постит POST /chains (кнопки Запустить/Отложить).
 * mode="template" — возвращает ChainTemplate через onChange (для расписаний/триггеров).
 */
export const ChainBuilder: FC<IProps> = (props) => {
  const isTemplate = props.mode === "template"
  const canWrite = useUserbotWriteAccess()
  const catalog = useCatalog()

  const descriptors = catalog.actions
  const descriptorsMap = useMemo<Descriptors>(
    () =>
      descriptors.reduce((acc, d) => {
        acc[d.name] = d
        return acc
      }, {} as Descriptors),
    [descriptors],
  )

  const [state, setState] = useState<ChainFormState>(
    (isTemplate && (props as TemplateModeProps).value) || initialState(),
  )
  const [advanced, setAdvanced] = useState(false)
  const current = useRef(state)
  current.current = state
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const [errors, setErrors] = useState<string[]>([])
  const [validation, setValidation] =
    useState<UserbotCatalog.ValidationResult | null>(null)
  const [snack, setSnack] = useState<{
    open: boolean
    variant: "success" | "error"
    message: string
  }>({ open: false, variant: "success", message: "" })

  const [createChain, { isLoading }] = useCreateChainMutation()
  const [validate, { isLoading: isValidating }] = useValidateChainMutation()

  /** Плейсхолдеры {{stepId.result.*}} для автокомплита (без собственного узла). */
  const getResultHints = useCallback(
    (currentNodeId: string) =>
      collectResultPaths(state.steps, descriptorsMap, currentNodeId),
    [state.steps, descriptorsMap],
  )

  const update = (over: Partial<ChainFormState>) => {
    const next = { ...state, ...over }
    setState(next)
    setValidation(null)
    if (isTemplate) {
      const errs = validateChainForTemplate(
        next,
        descriptorsMap,
        catalog.limits,
      )
      ;(props as TemplateModeProps).onChange(
        next,
        errs.length ? null : toChainTemplate(next, descriptorsMap),
      )
    }
  }

  /** Группировка issues по stepId для подсветки под шагом. */
  const issuesByStep = useMemo<
    Map<string, UserbotCatalog.ValidationIssue[]>
  >(() => {
    const map = new Map<string, UserbotCatalog.ValidationIssue[]>()
    if (validation && !validation.valid) {
      for (const issue of validation.errors) {
        if (!issue.stepId) continue
        const list = map.get(issue.stepId) ?? []
        list.push(issue)
        map.set(issue.stepId, list)
      }
    }
    return map
  }, [validation])

  const generalIssues = useMemo<UserbotCatalog.ValidationIssue[]>(
    () =>
      validation && !validation.valid
        ? validation.errors.filter((i) => !i.stepId)
        : [],
    [validation],
  )

  const effectiveLaunchDto = (schedule: boolean) => {
    const dto = toChainDto(state, descriptorsMap)
    if (!schedule) delete dto.scheduleDate
    return dto
  }

  // Called only by a lock-owning submit operation.
  const runDryRun = async (schedule: boolean): Promise<boolean> => {
    const errs = validateChain(state, descriptorsMap, catalog.limits)
    if (schedule && !state.scheduleDate)
      errs.push("Для отложенного запуска укажите дату")
    setErrors(errs)
    if (errs.length) return false
    try {
      const identity = launchIdentity(state)
      const dto = effectiveLaunchDto(schedule)
      const res = await validate({ body: dto }).unwrap()
      if (!mounted.current || launchIdentity(current.current) !== identity)
        return false
      setValidation(res)
      return res.valid
    } catch (e) {
      setSnack({ open: true, variant: "error", message: getApiErrorMessage(e) })
      return false
    }
  }

  const submit = async (schedule: boolean) => {
    if (!canWrite || inFlight.current) return
    const errs = validateChain(state, descriptorsMap, catalog.limits)
    if (schedule && !state.scheduleDate) {
      errs.push("Для отложенного запуска укажите дату")
    }
    setErrors(errs)
    if (errs.length) return

    const dto = effectiveLaunchDto(schedule)

    inFlight.current = true
    try {
      setBusy(true)
      const identity = launchIdentity(state)
      const ok = await runDryRun(schedule)
      if (
        !ok ||
        !mounted.current ||
        launchIdentity(current.current) !== identity
      )
        return
      const res = await createChain({ body: dto }).unwrap()
      setSnack({
        open: true,
        variant: "success",
        message: launchResultMessage(res),
      })
      setState(initialState())
      setErrors([])
      setValidation(null)
      if (props.mode === "run") props.onLaunched?.(res)
    } catch (e) {
      // 400 от zod-валидации chainTemplate (ответ бэка §VII.8)
      const apiErr = e as { data?: { message?: string[] | string } }
      const messages = apiErr?.data?.message
      if (messages) {
        const issues = mapZodErrors(messages)
        setValidation({ valid: false, errors: issues })
      }
      setSnack({ open: true, variant: "error", message: getApiErrorMessage(e) })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  if (catalog.isLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" py={4}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Загрузка каталога…</Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <SnackbarAlert
        isOpen={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        variant={snack.variant}
        title={snack.variant === "error" ? "Ошибка" : "Успешно"}
        message={snack.message}
      />

      <Stack direction="row" justifyContent="flex-end">
        <FormControlLabel
          control={
            <Switch
              checked={advanced}
              onChange={(e) => setAdvanced(e.target.checked)}
            />
          }
          label="Расширенный режим"
        />
      </Stack>

      <Paper sx={{ p: 2 }} variant="outlined">
        <Stack spacing={2}>
          <TextField
            label="Название цепочки"
            value={state.name}
            onChange={(e) => update({ name: e.target.value })}
            required
            fullWidth
          />
          {!isTemplate && (
            <LaunchTargetFields
              value={state}
              onChange={(target) =>
                update({
                  clientId: undefined,
                  role: undefined,
                  roleMode: undefined,
                  targetUnit: undefined,
                  targetFilter: undefined,
                  maxAgeDays: undefined,
                  ...target,
                })
              }
            />
          )}
          <TextField
            label="Комментарий"
            value={state.comment}
            onChange={(e) => update({ comment: e.target.value })}
            fullWidth
          />
          {!isTemplate && (
            <TextField
              label="Отложенный запуск (scheduleDate)"
              type="datetime-local"
              value={state.scheduleDate}
              onChange={(e) => update({ scheduleDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {advanced && (
            <TextField
              label="Контекст (JSON)"
              value={state.contextJson}
              onChange={(e) => update({ contextJson: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              InputProps={{ style: { fontFamily: "monospace" } }}
            />
          )}
        </Stack>
      </Paper>

      <Divider textAlign="left">
        <Typography variant="subtitle2">Узлы цепочки</Typography>
      </Divider>

      <Slot
        nodes={state.steps}
        descriptors={descriptors}
        nodeKinds={catalog.nodeKinds}
        limits={catalog.limits}
        advanced={advanced}
        extraHints={props.extraHints}
        getResultHints={getResultHints}
        onChange={(steps) => update({ steps })}
      />

      {errors.length > 0 && (
        <Alert severity="error">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      {validation?.valid && <Alert severity="success">Цепочка валидна</Alert>}

      {generalIssues.length > 0 && (
        <Alert severity="error">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {generalIssues.map((iss, i) => (
              <li key={i}>
                {iss.path ? `${iss.path}: ` : ""}
                {iss.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {!isTemplate && (
        <>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {canWrite && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  onClick={() => submit(true)}
                  disabled={isLoading || isValidating || busy}
                >
                  Отложить
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => submit(false)}
                  disabled={isLoading || isValidating || busy}
                >
                  Запустить
                </Button>
              </>
            )}
          </Stack>
        </>
      )}
    </Stack>
  )
}

/** Валидация шаблона — без исполнителя. */
const validateChainForTemplate = (
  state: ChainFormState,
  descriptors: Descriptors,
  limits?: UserbotCatalog.CatalogLimits,
): string[] =>
  validateChain(
    {
      ...state,
      executor: "client",
      clientId: "template-ok",
    },
    descriptors,
    limits,
  ).filter((e) => !e.includes("исполнителя"))
